import { FC, useEffect, useState } from 'react';
import { Link, Location, Outlet, useLocation, useNavigate } from 'react-router-dom';

// API
import apibridge from '../../../apibridge';

// REDUX
import { useAppDispatch, useAppSelector } from '../../../store/slice';
import { UserSlice, UserType } from '../../../store/slice/User';
import { ToastMessagesSlice } from '../../../store/slice/ToastMessages';
import { AlertSlice } from '../../../store/slice/Alert';

// COMPONENTS
import AlertBar from './AlertBar';
import SvgMask from '../../_Helpers/SvgMask';
import HubSpotContactModal from '../../HubSpotContactModal';

// UTILS
import ImgRem from '../../../libs/imgRem';
import { SystemInfoType } from '../../../store/slice/SystemInfo';
import { guid, getUserDomain, loadExternalScript } from '../../../libs/utils';
import { useHubSpotContactForm } from '../../../hooks/useHubSpotContactForm';
import { TrialTrialInfoTrialInfoResponse } from '../../../api/models/trial-trial-info-trial-info-response';
import OnBoardingFtux from '../../Ftux/OnboardingFtux';
import { useFtuxSync } from '../../Ftux';
// import PermissionsToggler from '../../../libs/PermissionsToggler';

const SideNavigationLink: FC<{
	id: string;
	label: string;
	urlPath: string;
	iconPath: string;
	location: Location<any>;
	user: UserType;
	isAlertLink?: boolean;
}> = ({ id = '', label, urlPath, iconPath, location, user, isAlertLink }) => {
	// because the 'Assessments' title and URL doesn't match up...
	const isAssessments = label === 'Assessments' && location.pathname.includes('/assessment-management');
	const isActiveLink = location.pathname.includes(urlPath) || isAssessments;

	const [userProfile = {}] = user.profiles || [];

	const hasActiveAlert = useAppSelector((state) => state.alert.hasActiveAlert);

	const showDot =
		(userProfile.type === 'OrganisationAdministrator' && !userProfile.staffAdded && id === 'link-teachers') ||
		(userProfile.type === 'Staff' && !userProfile.studentsAdded && id === 'link-students') ||
		(isAlertLink && hasActiveAlert);

	return (
		<Link
			id={id}
			className={`btn nav-link-text text-start ${isActiveLink ? 'active' : ''}`}
			to={urlPath}
			reloadDocument={isActiveLink}
		>
			<div className={`icon-wrapper d-flex align-items-center ${showDot ? 'show-dot' : ''} `}>
				<SvgMask path={iconPath} width={20} height={20} />
			</div>
			{label}
		</Link>
	);
};

const LayoutStaffDashboard: FC = () => {
	const navigate = useNavigate();
	const dispatch = useAppDispatch();
	const location = useLocation();

	const { isOpen, isSubmitted, openModal, closeModal, initializeForm } = useHubSpotContactForm();

	const user: UserType = useAppSelector((state) => state.user);
	const systemInfo: SystemInfoType = useAppSelector((state) => state.systemInfo);
	const [trialInfo, setTrialInfo] = useState<{
		info: TrialTrialInfoTrialInfoResponse;
	} | null>(null);

	const isTrialUser = systemInfo.isTrial;

	// Find active organisation
	const profiles = useAppSelector((state) => state.user.profiles);
	const activeProfileId = user.activeProfileId;
	const activeProfile = profiles?.find((profile) => profile.id === activeProfileId);
	const activeProfileName = activeProfile?.name;

	const isImpersonated = useAppSelector((state) => state.user.isImpersonated);

	const handleLogOut = () => {
		dispatch(UserSlice.actions.logOut());
		navigate('/login');
	};

	const getAlertActiveList = async () => {
		const response = await apibridge.getAlertActiveList();

		if (response && response.data) {
			if (!response.data.isError && response.data.result) {
				if (response.data.result.alerts && response.data.result.alerts.length > 0) {
					dispatch(AlertSlice.actions.activateAlertStatus());
				} else {
					dispatch(AlertSlice.actions.deactivateAlertStatus());
				}
			} else if (response.data.validationErrors) {
				for (const err of response.data.validationErrors) {
					dispatch(
						ToastMessagesSlice.actions.add({
							id: guid(),
							type: 'danger',
							heading: 'Alerts error',
							description: err.reason || 'Unknown error'
						})
					);
				}
			}
		}
	};

	const getTrialInfo = async () => {
		const response = await apibridge.getTrialInfo();
		if (response && response.data) {
			if (!response.data.isError && response.data.result) {
				setTrialInfo({ info: response.data.result });
			}
		} else if (response.data.validationErrors) {
			for (const err of response.data.validationErrors) {
				dispatch(
					ToastMessagesSlice.actions.add({
						id: guid(),
						type: 'danger',
						heading: 'Trial info error',
						description: err.reason || 'Unknown error'
					})
				);
			}
		}
	};

	// Sync FTUX status from user data to Redux
	useFtuxSync(user);

	const openContactSupportModal = () => {
		const supportUrl = trialInfo?.info?.supportLink?.url;

		if (supportUrl) {
			navigate(supportUrl);
		} else {
			openModal();
		}
	};

	useEffect(() => {
		getAlertActiveList();
		if (isTrialUser) {
			getTrialInfo();
		}
		// Load HubSpot form script for contact modal
		loadExternalScript('//js.hsforms.net/forms/embed/v2.js', 'hubspot-code');
	}, [isTrialUser]); // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<div
			className={`layout-staff-dashboard position-absolute w-100 h-100 d-flex ${
				isImpersonated ? 'impersonating' : ''
			} flex-column`}
		>
			{isImpersonated && (
				<div className="imptersonating-banner">
					<SvgMask path={'/svg/profile.svg'} width={20} height={20} />

					<p className="m-0">
						Impersonating{' '}
						<strong>
							{user.firstName} {user.lastName}
						</strong>
						{activeProfileName && (
							<>
								{' '}
								for <strong>{activeProfileName}</strong>
							</>
						)}
					</p>
				</div>
			)}
			<div className="d-flex flex-column flex-grow-1 outer-flex-wrapper">
				{isTrialUser && trialInfo && (
					<div className="container-fluid bg-dark-text">
						<div className="d-flex justify-content-center align-items-center  gap-3 px-3 py-2">
							<SvgMask path="/svg/timer.svg" width={20} height={20} className="text-white flex-shrink-0" />
							<span className="text-white">
								Trial ending in {trialInfo.info.expiryDays} day
								{trialInfo.info.expiryDays !== 1 ? 's' : ''}. Ready to sign up?
							</span>

							<Link
								to={`${getUserDomain('buyer')}/register?trialId=${user.activeOrganisationId}`}
								className="btn btn-sm btn-trial-banner"
							>
								Sign up
							</Link>
							<button type="button" className="btn btn-sm btn-trial-banner btn-white" onClick={openContactSupportModal}>
								Contact support
							</button>
						</div>
					</div>
				)}

				<div className="d-flex flex-grow-1 layout-content-wrapper">
					{/* Side Navigation */}
					<nav className="side-nav">
						<a href="/" className="logo-link">
							<ImgRem
								src="/svg/llll-online-wide-logo.svg"
								width="130"
								height="40"
								alt="Little Learners Love Literacy Online logo"
								className="logo mx-auto"
							/>
						</a>
						<div className="main-links-wrapper d-flex flex-column justify-space-between overflow-y-auto p-2">
							<div className="main-links flex-grow-1">
								{user.permissions?.some((permission) =>
									['PlannerWeek', 'RoutineGroups', 'BookList'].includes(permission)
								) && (
									<div className="pt-1 pb-2 d-flex flex-column">
										<div className="label-navigation text-hero-blue p-2">Teach</div>
										{user.permissions?.includes('PlannerWeek') && (
											<SideNavigationLink
												id="link-plans"
												label="Plans"
												urlPath="/plans"
												iconPath="/svg/plans-board.svg"
												location={location}
												user={user}
											/>
										)}
										{user.permissions?.includes('RoutineGroups') && (
											<SideNavigationLink
												id="link-routines"
												label="Routines"
												urlPath="/routines"
												iconPath="/svg/routines-class.svg"
												location={location}
												user={user}
											/>
										)}
										{user.permissions?.includes('BookList') && (
											<SideNavigationLink
												id="link-elibrary"
												label="eLibrary"
												urlPath="/elibrary"
												iconPath="/svg/library-read.svg"
												location={location}
												user={user}
											/>
										)}
									</div>
								)}
								{user.permissions?.some((permission) => ['ClassList', 'StaffDashboard'].includes(permission)) && (
									<div className="pt-1 pb-2 d-flex flex-column">
										<div className="label-navigation text-hero-blue p-2">Manage</div>
										{user.permissions?.includes('ClassList') && (
											<SideNavigationLink
												id="link-students"
												label="Students"
												urlPath="/student-management"
												iconPath="/svg/students-smile.svg"
												location={location}
												user={user}
											/>
										)}
										{user.permissions?.includes('StaffDashboard') && (
											<SideNavigationLink
												id="link-teachers"
												label="Teachers"
												urlPath="/teacher-management"
												iconPath="/svg/teachers-hat.svg"
												location={location}
												user={user}
											/>
										)}

										{user.permissions?.includes('Assessments') && (
											<SideNavigationLink
												id="link-assessments"
												label="Assessments"
												urlPath="/assessment-management?tabView=Dashboard"
												iconPath="/svg/checklist.svg"
												location={location}
												user={user}
											/>
										)}
									</div>
								)}
								{user.permissions?.some((permission) =>
									['ResourceFavorite', 'ProfessionalLearningList', 'HelpCentreList'].includes(permission)
								) && (
									<div className="pt-1 pb-2 d-flex flex-column">
										<div className="label-navigation text-hero-blue p-2">Support</div>
										{user.permissions?.includes('ResourceFavorite') && (
											<SideNavigationLink
												id="link-resources"
												label="Resources"
												urlPath="/resources"
												iconPath="/svg/resources-stack.svg"
												location={location}
												user={user}
											/>
										)}
										{user.permissions?.includes('ProfessionalLearningList') && (
											<SideNavigationLink
												id="link-professional-learning"
												label="Support"
												urlPath="/support"
												iconPath="/svg/skills-target.svg"
												location={location}
												user={user}
											/>
										)}
										{user.permissions?.includes('HelpCentreList') && (
											<SideNavigationLink
												id="link-help-centre"
												label="Help Centre"
												urlPath="/help-centre"
												iconPath="/svg/help-book.svg"
												location={location}
												user={user}
											/>
										)}
									</div>
								)}
							</div>
							<div className="secondary-links">
								<div className="pt-1 pb-2 d-flex flex-column">
									<SideNavigationLink
										id="link-alerts"
										label="Alerts"
										urlPath="/alerts"
										iconPath="/svg/bell.svg"
										location={location}
										user={user}
										isAlertLink
									/>

									<SideNavigationLink
										id="link-settings"
										label="Settings"
										urlPath="/settings"
										iconPath="/svg/settings.svg"
										location={location}
										user={user}
									/>

									{/* Used html button instead of react-bootstrap, to remove the btn class and make it look like a link */}
									<button type="button" className="btn nav-link-text" onClick={handleLogOut}>
										<SvgMask path={'/svg/sign-out.svg'} width={20} height={20} /> Log out
									</button>
								</div>
							</div>
						</div>
					</nav>

					{/* Main Content */}
					<main id="main-content" className="main-content d-flex flex-column flex-grow-1 overflow-auto">
						<AlertBar />
						{/* NOTE: This is useful for debugging permissions, and allows them to be switched on/off */}
						{/* For debugging only! */}
						{/* <PermissionsToggler /> */}
						<Outlet />
						{user.loggedIn && user.id && !user.ftuxCompleted && <OnBoardingFtux />}
					</main>
				</div>
			</div>
			<HubSpotContactModal
				isOpen={isOpen}
				onClose={closeModal}
				isSubmitted={isSubmitted}
				onShow={() => {
					initializeForm({
						region: 'na1',
						portalId: '8803439',
						formId: '47b507bd-37c3-4d4f-b1ac-458b326454c1',
						target: '#hubspot-target'
					});
				}}
			/>
		</div>
	);
};

export default LayoutStaffDashboard;
